import { CustomFontSize } from '../components/DialogHeader/DialogHeader';
import { ButtonSkin, Size } from '@wix/design-system';
import wixBg from '../../images/wix-bg.jpg';

type Skin = 'light' | 'dark' | 'standard'; // 'light' should be used on dark backgrounds
export type LogoPosition = 'left' | 'center';

type ButtonDesign = {
  skin: ButtonSkin;
  textKey?: string;
  color?: string;
  fullWidth?: boolean;
  height?: string;
  showArrowOnButton?: boolean;
  hideSuffixIcon?: boolean; // Hide the ChevronRight suffix icon on EmailStep button (default: false, shows icon for horizontal layouts)
};

type SocialButtonDesign = {
  borderColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  activeBackgroundColor?: string;
};

type FormSpacing = {
  inputButtonGap?: string; // Gap between input and button when no forgot email link (CSS value, e.g. '12px', '24px', default '24px')
  inputForgotEmailGap?: string; // Gap between input and forgot email link when present (CSS value, e.g. '8px', '12px', defaults to inputButtonGap)
  forgotEmailButtonGap?: string; // Gap between forgot email link and button (CSS value, e.g. '12px', '24px', default '24px')
  policiesDividerSpacing?: {
    top?: string; // Spacing above horizontal divider before policies (CSS value, e.g. '32px', '44px', default '32px')
    bottom?: string; // Spacing below horizontal divider before policies text (CSS value, e.g. '24px', '44px', default '24px')
  };
  titleSectionGap?: string; // Gap between title and subtitle in DialogHeader (CSS value, default: '3px' for vertical, 6px for horizontal)
  forgotEmailAlignment?: 'left' | 'center'; // Alignment of Forgot Email link in EmailStep (default: 'left' for horizontal, 'center' for vertical)
};

type Title = {
  key: string;
  font?: string;
  size?: Size;
};

type Container = {
  primary: {
    padding: string;
  };
  secondary: {
    padding: string;
  };
  borderRadius: number;
  boxShadow: string;
};

type AuthDialog = {
  title?: Title;
  subtitle?: Title;
  flows?: {
    id: string;
    title: Title;
    subtitle?: Title;
  }[];
  primaryButtonDesign?: ButtonDesign;
  secondaryButtonDesign?: ButtonDesign;
  emailLabelKey?: string;
};

type LoginAuthDialog = AuthDialog & {
  alreadyHaveAccount?: {
    title?: Title;
    subtitle?: Title;
  };
};

export const SocialProviders = {
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  APPLE: 'apple',
};

export const PRESETS = {
  DAYFUL: 'dayful',
  EDITORX: 'editorx',
  HOPP: 'hopp',
  EDUCATION_STUDENT: 'EDUCATION_STUDENT',
  EDUCATION_TEACHER: 'EDUCATION_TEACHER',
  CODUX: 'codux', // New branding for WixComponentsStudio (WCS)
  LOGO_MAKER: 'logo_maker',
  BUSINESS_CARD_MAKER: 'bcm',
  WIXEL: 'wixel',
  PICASSO: 'picasso',
  WIX_2025: 'wix-2025',
};

export type ViewPreset = Partial<{
  id: string; // Default - preset key
  nameKey: string; // Default - 'presets.names.wix' = Wix
  skin: Skin; // Default - standard
  shouldDisablePostLoginSuggestions: boolean; // Default - false
  container: {
    layout: 'vertical' | 'horizontal'; // Default - horizontal
    desktop: Container;
    mobile: Container;
  };
  background: string;
  login: LoginAuthDialog;
  signup: AuthDialog;
  landingPage: AuthDialog; // Main landing page (email step)
  logo: Partial<{
    // Default - wix logo
    src: string;
    position: LogoPosition; // Default - 'left'
    height: string; // Default - 24px
    mobile: {
      height: string; // Default - 20px
    };
    alt: string; // Default - 'Wix'
    url: string; // Default - www.wix.com
  }>;
  policies: {
    policyKey?: string; // Default - 'policies_section.signup_content'
    termsOfUseUrl?: string; // Default - wix terms of use
    privacyPolicyUrl?: string; // Default - wix privacy policy
  };
  buttonDesign?: ButtonDesign;
  socialButtonDesign?: SocialButtonDesign;
  formSpacing?: FormSpacing;
  // Social providers separated with comma (for example - 'google,facebook')
  // Default - all ('google,facebook','apple')
  socialProviders: string;
  socialProvidersNewDesign: boolean;
  titleCustomFontSize: {
    authPage: {
      desktop: CustomFontSize;
      mobile: CustomFontSize;
    };
    secondaryFlow: {
      desktop: CustomFontSize;
      mobile: CustomFontSize;
    };
  };
  cssVariables: Record<string, string>;
}>;

export const skinToColor = {};
export const DEFAULT_LOGO_HEIGHT = '26px';

export const ViewPresets: {
  [key: string]: ViewPreset;
} = {
  [PRESETS.DAYFUL]: {
    nameKey: 'presets.names.dayful',
    landingPage: {
      title: {
        key: 'emailStep.dayful.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
      subtitle: {
        key: 'dayful_subtitle',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/dayful-logo.png',
      height: '34px',
      url: 'https://www.dayful.com/',
    },
  },
  [PRESETS.HOPP]: {
    nameKey: 'presets.names.hopp',
    landingPage: {
      title: {
        key: 'emailStep.hopp.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
      subtitle: {
        key: 'hopp_subtitle',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/Hopp%20by%20wix%20logo%20(1).svg',
      height: '22px',
      url: 'https://www.hopp.co/',
    },
  },
  [PRESETS.EDUCATION_STUDENT]: {
    id: PRESETS.EDUCATION_STUDENT,
    nameKey: 'presets.names.education',
    landingPage: {
      title: {
        key: 'emailStep.education.student.title',
      },
    },
    login: {
      subtitle: {
        key: 'login.education.subtitle',
      },
    },
    signup: {
      title: {
        key: 'signup.education.student.title',
      },
    },
    logo: {
      src: 'https://images-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/642436ff-70f5-4ebc-8fea-47fab46f7f73/1693247680916',
      url: 'https://www.wix.com/education',
    },
    policies: {
      termsOfUseUrl:
        'https://www.wix.com/tomorrow/classroom/platform/terms-of-use',
      privacyPolicyUrl:
        'https://www.wix.com/tomorrow/classroom/platform/privacy-policy',
    },
    socialProviders: SocialProviders.GOOGLE,
  },
  [PRESETS.EDUCATION_TEACHER]: {
    id: PRESETS.EDUCATION_TEACHER,
    nameKey: 'presets.names.education',
    landingPage: {
      title: {
        key: 'emailStep.education.teacher.title',
      },
    },
    login: {
      subtitle: {
        key: 'login.education.subtitle',
      },
    },
    signup: {
      title: {
        key: 'signup.education.teacher.title',
      },
    },
    logo: {
      src: 'https://images-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/642436ff-70f5-4ebc-8fea-47fab46f7f73/1693247680916',
      url: 'https://www.wix.com/education',
    },
    policies: {
      termsOfUseUrl:
        'https://www.wix.com/tomorrow/classroom/platform/terms-of-use',
      privacyPolicyUrl:
        'https://www.wix.com/tomorrow/classroom/platform/privacy-policy',
    },
    socialProviders: SocialProviders.GOOGLE,
  },
  [PRESETS.CODUX]: {
    logo: {
      src: 'https://wixplosives.github.io/codux-assets-storage/login/codux-logo.svg',
      position: 'center',
      height: '100px',
      mobile: {
        height: '80px',
      },
      url: 'https://www.codux.com/',
    },
    signup: {
      title: {
        key: 'signup.title',
      },
    },
    policies: {
      termsOfUseUrl: 'https://www.wix.com/about/terms-of-use',
      privacyPolicyUrl: 'https://www.wix.com/about/privacy',
    },
  },
  [PRESETS.EDITORX]: {
    nameKey: 'presets.names.editorx',
    landingPage: {
      title: {
        key: 'emailStep.editorx.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/EditorX_logo_36x168_black.svg',
      url: 'https://www.editorx.com/',
    },
  },
  [PRESETS.LOGO_MAKER]: {
    nameKey: 'presets.names.logo_maker',
    logo: {
      url: 'https://www.wix.com/logo/maker/',
    },
    landingPage: {
      title: {
        key: 'emailStep.logo_maker.title',
      },
    },
    login: {
      title: {
        key: 'login.logo_maker.title',
      },
    },
    signup: {
      title: {
        key: 'signup.logo_maker.title',
      },
    },
  },
  [PRESETS.BUSINESS_CARD_MAKER]: {
    nameKey: 'presets.names.business_card_maker',
    logo: {
      url: 'https://www.wix.com/tools/business-card-maker/',
    },
    login: {
      title: {
        key: 'login.business_card_maker.title',
      },
    },
    signup: {
      title: {
        key: 'signup.business_card_maker.title',
      },
    },
  },

  [PRESETS.WIXEL]: {
    id: PRESETS.WIXEL,
    shouldDisablePostLoginSuggestions: true,
    nameKey: 'presets.names.wixel',
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 52px 0',
        },
        borderRadius: 24,
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: 24,
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wixel-logo.svg',
      url: 'https://www.wix.com/wixel',
      height: '28px',
      alt: 'Wixel',
      mobile: {
        height: '24px',
      },
    },
    landingPage: {
      title: {
        key: 'emailStep.wixel.title',
      },
    },
    socialProvidersNewDesign: true,
    login: {
      title: {
        key: 'login.wixel.title',
      },
      subtitle: {
        key: 'login.wixel.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.wixel.primary_button',
        skin: 'standard',
        fullWidth: true,
      },
      alreadyHaveAccount: {
        title: {
          key: 'login.wixel.already_have_account',
        },
      },
    },
    signup: {
      title: {
        key: 'signup.wixel.title',
      },
      subtitle: {
        key: 'signup.wixel.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.wixel.primary_button',
        skin: 'standard',
        fullWidth: true,
      },
      flows: [
        {
          id: 'image',
          title: {
            key: 'signup.wixel.image_flow.title',
          },
          subtitle: {
            key: 'signup.wixel.image_flow.subtitle',
          },
        },
      ],
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      showArrowOnButton: true,
    },
    titleCustomFontSize: {
      authPage: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
      secondaryFlow: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
    },
    cssVariables: {
      '--wds-loader-fill-loading': '#D6FFE1',
      '--wds-loader-fill': '#1AAC40',
      '--wds-button-fill-standard-primary': '#442894',
      '--wds-button-fill-standard-primary-hover': '#442894',
      '--wds-button-fill-standard-primary-active': '#442894',
      '--wds-button-fill-standard-secondary-hover': '#442894',
      '--wds-button-fill-standard-secondary-active': '#442894',
      '--wds-color-text-primary': 'black',
      '--wds-font-weight-medium': '400',
      '--wds-font-weight-heading-1': '400',
      '--wds-font-weight-heading-2': '400',
      '--wds-font-weight-heading-3': '400',
      '--wds-color-border-standard-secondary': '#442894',
      '--wds-color-fill-standard-tertiary-hover': '#ffffff',
      '--wds-font-line-height-body-tiny': '18px',
      '--wds-font-family-display': 'Madefor Display',
    },
    background: `linear-gradient(144.34deg, rgba(205, 230, 255, 0.7) 31.48%, #8FF4AA 120.33%), linear-gradient(180deg, #ADD7FF 61.16%, #D3EAFF 100%)`,
    policies: {
      policyKey: 'policies_section.signup.wixel',
    },
  },
  [PRESETS.PICASSO]: {
    id: PRESETS.PICASSO,
    shouldDisablePostLoginSuggestions: true,
    nameKey: 'presets.names.wixel',
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 52px 0',
        },
        borderRadius: 24,
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: 24,
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/picasso-logo.png',
      url: 'https://www.wix.com/picasso',
      height: '90px',
      alt: 'Picasso',
      mobile: {
        height: '24px',
      },
    },
    landingPage: {
      title: {
        key: 'emailStep.picasso.title',
      },
    },
    socialProvidersNewDesign: true,
    login: {
      title: {
        key: 'login.picasso.title',
      },
      subtitle: {
        key: 'login.picasso.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.picasso.primary_button',
        skin: 'dark',
        fullWidth: true,
      },
      alreadyHaveAccount: {
        title: {
          key: 'login.picasso.already_have_account',
        },
      },
    },
    signup: {
      title: {
        key: 'signup.picasso.title',
      },
      subtitle: {
        key: 'signup.picasso.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.picasso.primary_button',
        skin: 'dark',
        fullWidth: true,
      },
      flows: [
        {
          id: 'image',
          title: {
            key: 'signup.picasso.image_flow.title',
          },
          subtitle: {
            key: 'signup.picasso.image_flow.subtitle',
          },
        },
      ],
    },
    buttonDesign: {
      skin: 'dark',
      fullWidth: true,
      showArrowOnButton: true,
    },
    cssVariables: {
      '--wds-loader-fill-loading': '#D6FFE1',
      '--wds-loader-fill': '#1AAC40',
    },
    background: `url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/picasso-background.jpeg")`,
    policies: {
      policyKey: 'policies_section.signup.picasso',
    },
  },
  [PRESETS.WIX_2025]: {
    id: PRESETS.WIX_2025,
    shouldDisablePostLoginSuggestions: true,
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 52px 0',
        },
        borderRadius: 12,
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: 12,
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
    },
    logo: {
      height: '29px',
      mobile: {
        height: '18px',
      },
    },
    socialProvidersNewDesign: true,
    socialButtonDesign: {
      borderColor: '#000000', // Black border per Figma (node 3808-12633)
      borderWidth: '1px', // Per Figma spec
      borderRadius: '6px', // Per Figma spec
    },
    formSpacing: {
      inputButtonGap: '12px', // 12px gap between input and button when no forgot email link (Figma node 3808-12377)
      inputForgotEmailGap: '8px', // 8px gap between input and forgot email link when present
      forgotEmailButtonGap: '24px', // 24px gap between forgot email link and button
      policiesDividerSpacing: {
        top: '44px', // 44px above the horizontal divider (Figma node 3808-12646)
        bottom: '44px', // 44px below the horizontal divider before policy text (Figma node 3808-12648)
      },
      titleSectionGap: '16px', // 16px gap between title and subtitle (Figma node 3808-12463)
      forgotEmailAlignment: 'center', // Center Forgot Email link to match Figma
    },
    landingPage: {
      emailLabelKey: 'emailStep.wix2025.email.label',
      primaryButtonDesign: {
        textKey: 'enter_email.send', // "Continue"
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true, // Hide ChevronRight icon to match Figma
      },
    },
    login: {
      emailLabelKey: 'login.wix2025.email.label',
      primaryButtonDesign: {
        textKey: 'enter_email.send', // "Continue"
        skin: 'standard',
        fullWidth: true,
      },
    },
    signup: {
      emailLabelKey: 'signup.wix2025.email.label',
      primaryButtonDesign: {
        textKey: 'enter_email.send', // "Continue"
        skin: 'standard',
        fullWidth: true,
      },
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      height: '43px', // Figma spec from node 3808-12631
      showArrowOnButton: false,
    },
    titleCustomFontSize: {
      authPage: {
        desktop: 'xLarge', // 36px with 16px margin-bottom (Figma node 3808-12631)
        mobile: 'xLarge', // 28px with 12px margin-bottom
      },
      secondaryFlow: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
    },
    cssVariables: {
      // Primary Button colors (Figma nodes: 3808-15760, 3808-16879, 3808-16886, 3808-16893)
      '--wds-button-fill-standard-primary': '#2f5dff', // Regular state (node 3808-15760)
      '--wds-button-fill-standard-primary-hover': '#1c3fe5', // Hover (node 3808-16879)
      '--wds-button-fill-standard-primary-active': '#0f2ccf', // Pressed (node 3808-16886)

      // Button border radius - override WDS default pill shape to rounded rectangle
      // Figma shows moderate border radius (node 3808-12628)
      '--wds-button-border-radius-medium': '8px',

      // Font family - Madefor Display for headings (Figma node 3808-12631)
      '--wds-font-family-display': 'Madefor Display',

      // NOTE: These CSS variables follow the wixel preset pattern.
      // Additional button states (disabled, secondary) and input field colors
      // cannot be configured via CSS variables and require component-level SCSS overrides.
      // See docs/wix-2025-preset-notes.md for details on non-configurable styles.
    },
    // Background: wix-bg still image centered without scaling; surplus space uses #E6E2E3
    background: `#E6E2E3 url("${wixBg}") center center/auto no-repeat`,
  },
};
